import { FC, useEffect, useRef, useState } from "react";
import { Form, ListGroup, Spinner } from "react-bootstrap";
import FlexBox from "../flex-box";
import { PiArrowRight, PiEyeBold } from "react-icons/pi";
import { getUniqueId, openUrlInNewTab } from "lib/utils/util";
import RequiredField from "../required-field";
import { DOCUMENT_TYPES } from "lib/constants/enums";
import SimpleButton from "../simple-button";

interface SimpleDocSelectorProps {
  isLoading?: boolean;
  isEditable?: boolean;
  isClearOnSelect?: boolean;
  isSelectMultiple?: boolean;
  label?: string;
  title?: string;
  onChange?: (files: File[] | null) => void;
  required?: boolean;
  docType: "IMAGES" | "DOCUMENTS";
  docUrl?: string;
}

const SimpleDocSelector: FC<SimpleDocSelectorProps> = ({
  isLoading,
  isEditable = true,
  isClearOnSelect = false,
  isSelectMultiple = false,
  label,
  title = "Select file",
  onChange,
  required,
  docType,
  docUrl,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  useEffect(() => {
    if (docUrl) {
      setDocumentUrl(docUrl);
    }
  }, [docUrl]);

  const handlePreview = () => {
    if (selectedFile) {
      const fileUrl = URL.createObjectURL(selectedFile);
      openUrlInNewTab(fileUrl);
    } else if (documentUrl) {
      openUrlInNewTab(documentUrl);
    }
  };

  const getFileNameToDisplay = () => {
    if (selectedFile) return selectedFile?.name;
    if (documentUrl) return documentUrl;

    return title;
  };

  return (
    <>
      {label && (
        <Form.Label className="mt-2">
          {label}
          {required && (
            <>
              &nbsp;
              <RequiredField />
            </>
          )}
        </Form.Label>
      )}
      <ListGroup className="hover-shadow">
        <input
          type="file"
          accept={DOCUMENT_TYPES[docType]}
          id={getUniqueId()}
          ref={fileRef}
          multiple={isSelectMultiple}
          className="d-none"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            onChange?.(files?.length > 0 ? files : null);
            setSelectedFile(
              isClearOnSelect ? null : e.target.files?.[0] ?? null
            );
          }}
        />
        <ListGroup.Item
          className={`cursor-pointer ${
            selectedFile || documentUrl
              ? "doc-list-item-with-doc"
              : "doc-list-item"
          }`}
          onClick={() => {
            if (isEditable) {
              fileRef.current?.click();
            }
          }}
        >
          {(documentUrl || selectedFile) && (
            <SimpleButton
              isLoading={isLoading}
              variant="primary"
              className="me-2"
              onClick={handlePreview}
              size="sm"
            >
              <PiEyeBold />
            </SimpleButton>
          )}
          <FlexBox justifyContent="start" className="text-primary">
            <span className="truncate w-100">{getFileNameToDisplay()}</span>
            <span className="">
              {isLoading ? (
                <Spinner
                  animation="border"
                  variant="warning"
                  className="loading"
                />
              ) : (
                <PiArrowRight />
              )}
            </span>
          </FlexBox>
        </ListGroup.Item>
      </ListGroup>
    </>
  );
};
export default SimpleDocSelector;
